import type { WorkerContract, WorkerContractSchemas, WorkerManifest } from './contract';
/*
 * Payload codecs (ADR 0016 wire protocol): exactly two are supported. `structuredClone` passes
 * payloads through `postMessage` untouched; `zod` encodes/decodes through the contract's
 * schemas. The legacy `@thinkalpha/common` replacer/reviver serializer is banned on this
 * protocol. Frame *envelopes* are always zod-decoded regardless of the payload codec.
 *
 * The codec surface is deliberately type-erased: it sits under the typed client/impl surfaces,
 * where request types arrive as wire strings. The `zod` codec restores real validation; the
 * `structuredClone` codec is a declared passthrough.
 */
import { z } from 'zod';
import type { ZodType } from 'zod';

export interface WorkerPayloadCodec {
    encodeRequest(type: string, payload: unknown): unknown;
    decodeRequest(type: string, data: unknown): unknown;
    encodeResponse(type: string, payload: unknown): unknown;
    decodeResponse(type: string, data: unknown): unknown;
    encodeEvent(type: string, payload: unknown): unknown;
    decodeEvent(type: string, data: unknown): unknown;
}

interface ErasedRequestSchemas {
    readonly request: ZodType<unknown>;
    readonly response: ZodType<unknown>;
}

const structuredCloneCodec: WorkerPayloadCodec = {
    encodeRequest: (_type, payload) => payload,
    decodeRequest: (_type, data) => data,
    encodeResponse: (_type, payload) => payload,
    decodeResponse: (_type, data) => data,
    encodeEvent: (_type, payload) => payload,
    decodeEvent: (_type, data) => data,
};

const createZodCodec = <C extends WorkerContract>(schemas: WorkerContractSchemas<C>): WorkerPayloadCodec => {
    const requests: Partial<Record<string, ErasedRequestSchemas>> = schemas.requests;
    const events: Partial<Record<string, ZodType<unknown>>> = schemas.events;

    // Own-property lookups only: wire types are attacker-adjacent input, and a plain index would
    // resolve inherited names ('constructor', 'toString') through the prototype chain.
    const requestSchemas = (type: string): ErasedRequestSchemas => {
        const entry = Object.hasOwn(requests, type) ? requests[type] : undefined;
        if (!entry) {
            throw new Error(`No schema declared for worker request type '${type}'`);
        }
        return entry;
    };

    const eventSchema = (type: string): ZodType<unknown> => {
        const schema = Object.hasOwn(events, type) ? events[type] : undefined;
        if (!schema) {
            throw new Error(`No schema declared for worker event type '${type}'`);
        }
        return schema;
    };

    return {
        encodeRequest: (type, payload) => z.encode(requestSchemas(type).request, payload),
        decodeRequest: (type, data) => z.decode(requestSchemas(type).request, data),
        encodeResponse: (type, payload) => z.encode(requestSchemas(type).response, payload),
        decodeResponse: (type, data) => z.decode(requestSchemas(type).response, data),
        encodeEvent: (type, payload) => z.encode(eventSchema(type), payload),
        decodeEvent: (type, data) => z.decode(eventSchema(type), data),
    };
};

export const createWorkerPayloadCodec = <C extends WorkerContract>(manifest: WorkerManifest<C>): WorkerPayloadCodec =>
    manifest.codec === 'zod' ? createZodCodec(manifest.schemas) : structuredCloneCodec;
